import { useQuery } from "@tanstack/react-query";
import { type ReactNode, useId, useState } from "react";
import { z } from "zod";
import {
  type DeferredQuestion,
  type DraftNote,
  deferredQuestionLabel,
  OfferPassageSchema,
  type OfferSourceLine,
  offerPassageNote,
  offerSourceFact,
  offerSourceToggle,
} from "../src/draft-source-line.ts";
import { AppLink, answersFocusPath, useWorkspace } from "./workspace-context.tsx";

/**
 * One line under a draft: a label, a plain fact, and one text button that opens its words in place
 * (docs/2026-09-29-draft-offer-source.md). The offer's source and a question the draft deferred use
 * this same line, so a rep reads both the same way.
 *
 * The words are rendered only while open; `body` is called then, so a caller that fetches them
 * fetches on the first open and not before.
 */
export function DraftSourceLine({
  label,
  fact,
  toggle,
  body,
  action,
}: {
  readonly label: string;
  readonly fact: ReactNode;
  readonly toggle: (open: boolean) => string;
  readonly body: () => ReactNode;
  /** A second control after the toggle, such as "Add answer". */
  readonly action?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const region = useId();
  return (
    <div className="lcr-draft-source">
      <p className="lcr-draft-source-line">
        <span className="lcr-draft-source-label">{label}:</span> {fact}
        <span className="lcr-draft-source-controls">
          <button
            type="button"
            className="lcr-draft-source-toggle"
            aria-expanded={open}
            aria-controls={region}
            onClick={() => setOpen((value) => !value)}
          >
            {toggle(open)}
          </button>
          {action}
        </span>
      </p>
      {open && (
        <div className="lcr-draft-source-body" id={region}>
          {body()}
        </div>
      )}
    </div>
  );
}

const PassageResponseSchema = z.object({ passage: OfferPassageSchema });

/** The offer's words, read from the server when the rep first opens them. */
function OfferPassage({
  planApi,
  line,
  when,
}: {
  readonly planApi: string;
  readonly line: OfferSourceLine;
  readonly when: (at: string) => string;
}) {
  const passage = useQuery({
    queryKey: ["offer-passage", planApi, line.draftRevision],
    queryFn: async () => {
      const response = await fetch(`${planApi}/offer-source/${line.draftRevision}`, {
        credentials: "same-origin",
      });
      if (!response.ok) throw new Error("offer_passage_unavailable");
      return PassageResponseSchema.parse(await response.json()).passage;
    },
    staleTime: 60_000,
  });
  if (passage.isPending) return <p className="lcr-draft-source-note">Reading the words…</p>;
  if (passage.isError)
    return (
      <p className="lcr-draft-source-note" role="alert">
        The words could not be read.{" "}
        <button
          type="button"
          className="lcr-draft-source-toggle"
          onClick={() => void passage.refetch()}
        >
          Try again
        </button>
      </p>
    );
  const note = offerPassageNote(line, passage.data);
  const found = passage.data.state === "found" ? passage.data : null;
  return (
    <>
      {found && <blockquote className="lcr-draft-source-words">{found.text}</blockquote>}
      {found?.messageId && found.at && (
        <p className="lcr-draft-source-note">
          Sent {when(found.at)} · <a href={`#message-${found.messageId}`}>Go to the email</a>
        </p>
      )}
      {note && <p className="lcr-draft-source-note">{note}</p>}
    </>
  );
}

/** "Offer: from the email this lead was sent", under a draft that states an offer. */
export function OfferSourceLineView({
  planApi,
  line,
  when,
}: {
  readonly planApi: string;
  readonly line: OfferSourceLine;
  readonly when: (at: string) => string;
}) {
  return (
    <DraftSourceLine
      label="Offer"
      fact={offerSourceFact(line)}
      toggle={(open) => offerSourceToggle(line, open)}
      body={() => <OfferPassage planApi={planApi} line={line} when={when} />}
    />
  );
}

/**
 * "Pricing: pointed to the call, no saved answer", under a draft that deferred a question. Add answer
 * opens the Standing answers form for the campaign, where an answer for the next lead is saved.
 */
export function DeferredQuestionLineView({
  question,
  campaignId,
}: {
  readonly question: DeferredQuestion;
  readonly campaignId: string;
}) {
  const workspace = useWorkspace();
  return (
    <DraftSourceLine
      label={deferredQuestionLabel(question.ask)}
      fact={question.note}
      toggle={(open) => `${open ? "Hide" : "Show"} sentence`}
      body={() =>
        question.sentence ? (
          <blockquote className="lcr-draft-source-words">{question.sentence}</blockquote>
        ) : (
          <p className="lcr-draft-source-note">The draft names no sentence for it.</p>
        )
      }
      action={
        <AppLink
          className="lcr-draft-source-toggle"
          href={answersFocusPath(workspace.id, { key: "answers_when_asked", campaignId })}
        >
          Add answer
        </AppLink>
      }
    />
  );
}

/**
 * "Note: …" under a draft: one sentence from the check that reads the draft's prose, about a fact
 * it names. It asks nothing, so it has no control and takes the quiet register of the lines above
 * it, never a hold's or a question's.
 */
export function DraftNoteLine({ note }: { readonly note: DraftNote }) {
  return (
    <div className="lcr-draft-source lcr-draft-note">
      <p className="lcr-draft-source-line">
        <span className="lcr-draft-source-label">Note:</span> {note.text}
      </p>
    </div>
  );
}
